// Reviewer-facing SMS/RCS terms shared verbatim by the Terms of Service
// (section 2) and the Messaging Terms page so the two can never drift.
export function SmsRcsMessagingTermsContent({
  makeId,
}: {
  makeId: (suffix: string) => string;
}) {
  return (
    <>
      <p>
        These SMS/RCS Messaging Terms apply when you opt in to receive text,
        SMS, or RCS messages from Logical Health, Inc. They are part of
        Logical Health&rsquo;s{" "}
        <a
          href="/legal#terms-of-service"
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          Terms of Service
        </a>{" "}
        and supplement our{" "}
        <a
          href="/legal#privacy-policy"
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          Privacy Policy
        </a>
        . For customer deployments involving protected health information
        (&ldquo;PHI&rdquo;) or electronic protected health information
        (&ldquo;ePHI&rdquo;), message content, consent records, and related
        handling may also be governed by the applicable customer agreement,
        Business Associate Agreement, health-plan instructions, HIPAA/HITECH,
        and applicable law.
      </p>

      <h3
        id={makeId("program-and-purpose")}
        className="text-2xl font-bold text-gray-900 mb-4 mt-8"
      >
        Messaging Program and Purpose
      </h3>
      <p>
        Logical Health provides service and transactional SMS/RCS messaging
        for two-way healthcare benefits and member support. Depending on the
        program and your request, messages may include benefits and coverage
        support, claims explanations, copay or cost-estimate support,
        provider search, appointment navigation, appointment reminders,
        preventive-care reminders, medication reminders, support follow-up,
        consent confirmations, HELP responses, STOP confirmations, and
        related service messages.
      </p>
      <p className="mt-4">
        Logical Health does not use SMS/RCS messaging to send third-party
        promotional or marketing messages.
      </p>
      <p className="mt-4">
        No mobile information will be shared with third party/affiliates for
        marketing or promotional purposes.
      </p>

      <h3
        id={makeId("consent-and-opt-in")}
        className="text-2xl font-bold text-gray-900 mb-4 mt-8"
      >
        Consent and Opt-In
      </h3>
      <p>
        Logical Health sends SMS/RCS messages only to users who have opted in
        through a documented consent method, such as a web form and/or
        text-message keywords.
      </p>
      <p className="mt-4">
        Messages may be sent using automated technology. Consent to SMS/RCS
        messaging is not a condition of purchase. Logical Health does not
        send SMS/RCS messages to purchased, rented, or third-party contact
        lists.
      </p>

      <h3
        id={makeId("frequency-rates-availability")}
        className="text-2xl font-bold text-gray-900 mb-4 mt-8"
      >
        Message Frequency, Rates, and Availability
      </h3>
      <p>
        Message frequency varies based on your activity, requests, support
        needs, reminders, and service follow-up. Message and data rates may
        apply.
      </p>
      <p className="mt-4">
        SMS/RCS availability, features, delivery speed, and delivery
        confirmation depend on your carrier, device, messaging application,
        and region. Not all carriers or devices support RCS. Carriers are not
        liable for delayed or undelivered messages.
      </p>

      <h3
        id={makeId("opt-out-and-help")}
        className="text-2xl font-bold text-gray-900 mb-4 mt-8"
      >
        Opt-Out and Help
      </h3>
      <p>
        Reply STOP at any time to unsubscribe from Logical Health SMS/RCS
        messages. After you opt out, Logical Health will suppress SMS/RCS
        messages to your number unless you re-subscribe by texting START,
        LOGIC, or JOIN, or through another documented confirmation flow.
      </p>
      <p className="mt-4">
        Reply HELP at any time for assistance. You may also contact{" "}
        <a
          href="mailto:compliance@logical.health"
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          compliance@logical.health
        </a>{" "}
        for privacy, consent, opt-in, or opt-out inquiries.
      </p>

      <h3
        id={makeId("privacy-and-sharing")}
        className="text-2xl font-bold text-gray-900 mb-4 mt-8"
      >
        Privacy and Sharing of SMS/RCS Information
      </h3>
      <p>
        Logical Health collects and uses SMS/RCS information only as needed
        to operate, secure, support, document, and comply with the messaging
        program. This may include your phone number, opt-in method, consent
        status, disclosure version, timestamps, HELP and STOP requests,
        message metadata, and related records.
      </p>
      <p className="mt-4">
        Logical Health does not sell, rent, buy, or share SMS/RCS opt-in
        consent data or mobile information with third parties or affiliates
        for their own marketing or promotional purposes.
      </p>
      <p className="mt-4">
        Logical Health may disclose SMS/RCS information to service providers,
        messaging delivery vendors, Google, carriers, or other necessary
        parties only as needed to deliver, route, secure, support, bill,
        audit, and document the messaging program, comply with law, prevent
        spam or abuse, or honor your choices.
      </p>

      <h3
        id={makeId("rcs-for-business-processing")}
        className="text-2xl font-bold text-gray-900 mb-4 mt-8"
      >
        RCS for Business Processing
      </h3>
      <p>
        For RCS messages, Google, carriers, messaging providers, and their
        service providers may process message data and related records as
        needed to deliver and operate RCS for Business, maintain security,
        prevent spam and abuse, support billing or carrier review, and comply
        with applicable requirements.
      </p>
      <p className="mt-4">
        By participating in a Logical Health RCS messaging program, you
        authorize Logical Health and its messaging providers to send and
        receive RCS/SMS messages to and from your mobile number for the
        program you selected.
      </p>

      <h3
        id={makeId("double-opt-in")}
        className="text-2xl font-bold text-gray-900 mb-4 mt-8"
      >
        Double Opt-In
      </h3>
      <p>
        For certain programs or customer deployments, Logical Health may use
        a confirmation (double opt-in) flow, in which the user receives a
        prompt and replies YES or Y to confirm enrollment before program
        messaging begins. Where a confirmation flow is used, the confirmation
        reply is recorded as part of the consent record.
      </p>
    </>
  );
}
